import React, { FunctionComponent, ChangeEvent, useState } from 'react';
import styled from 'styled-components';

import ApolloClient from '@apollo/client';
import { ApolloConsumer } from '@apollo/client';
import { Register } from '../login/Register';
import { amsLogin, authLogin, getLostPasswordUrl } from '../login/auth';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '../login/ExaprintLoginForm';

const icnLoginError = require('../../assets/icons/error.svg');

interface Props {
  callback?: any;
  saveRegister?: boolean;
  title?: string;
  isOldVersion?: boolean;
}

/**
 * Login Form used in the Product PriceGrid for the attribute-based products
 *
 * @param callback The action which will be trigered after the login success
 * @param saveRegister This flag handles actions after registration
 * @param title
 * @param isOldVersion To flag that this will be used on the old version of the website.
 */
export const EmbeddedLoginForm: FunctionComponent<Props> = ({ callback, saveRegister, title, isOldVersion }) => {
  const { t } = useTranslation('sharedComponents');
  const [identifier, setIdentifier] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<boolean>();
  const [remember, setRemember] = useState<boolean>(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const name: string = e.target.name;
    const value: string = e.target.value;
    if (name === 'identifier') {
      setIdentifier(value);
    } else if (name === 'password') {
      setPassword(value);
    }
  };

  const handleRemember = () => {
    setRemember(!remember);
  };

  const login = async (client: ApolloClient.ApolloClient<object>) => {
    setLoading(true);
    const isPrintbox = 'goodies.printbox-commande.fr' === window.location.hostname;
    if (isPrintbox) {
      await amsLogin(
        {
          login: identifier,
          password
        },
        async () => {
          await client.resetStore();
          callback && callback();
        },
        () => {
          setError(true);
        }
      );
    } else {
      await authLogin(
        {
          identifiant: identifier,
          mot_de_passe: password
        },
        client,
        () => {
          callback && callback();
        },
        () => {
          setError(true);
        }
      );
    }

    setLoading(false);
  };
  return (
    <FormContainer>
      <FormBackground>
        <Form>
          <FormIntro>{title}</FormIntro>
          {error ? <FormErrors className="has-text-danger">{t('shared.front.loginform.error')}</FormErrors> : ''}
          <FormInput placeholder={`E-mail`} name="identifier" value={identifier} onChange={handleChange} />
          <FormInput type="password" placeholder={`Mot de passe`} name="password" value={password} onChange={handleChange} />
          <FormOptions>
            <Checkbox label={t('shared.loginform.stayloggedin')} value="remember" checked={remember} onChange={handleRemember} />
            <FormForgotPass>
              <FormPassLink href={getLostPasswordUrl()}>{t('shared.front.loginform.forgotpassword')}</FormPassLink>
            </FormForgotPass>
          </FormOptions>

          <ApolloConsumer>
            {client => (
              <FormConnection disabled={loading} loading={loading} name="login" onClick={() => login(client)}>
                {loading ? <Spinner /> : t('shared.product.login')}
              </FormConnection>
            )}
          </ApolloConsumer>
          {!isOldVersion && <FormRegister saveRegister={saveRegister} />}
        </Form>
      </FormBackground>
    </FormContainer>
  );
};

export const FormContainer = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
`;

export const FormBackground = styled.div`
  margin: auto;
  margin-top: 70px;
  width: 60%;
  box-sizing: border-box;
  padding: 30px 60px;
  text-align: center;
  font-family: var(--font-styrene);

  @media (max-width: 767px) {
    width: 93%;
    padding: 30px 15px;
  }
`;

const Form = styled.div``;

const FormOptions = styled.div`
  display: flex;
`;

const FormIntro = styled.div`
  font-size: 20px;
  line-height: 21px;
  margin-bottom: 20px;
  text-transform: none;
  text-align: center;
  color: var(--main-text);
  padding: 0;
  font-weight: 700;
`;

const FormInput = styled.input`
  width: 100%;
  margin-bottom: 10px;
  padding: 12px 4%;
  border: 1px solid #eee;

  &:focus {
    outline: #ccc solid 1px;
  }
`;

const FormForgotPass = styled.div`
  text-align: right;
  width: 100%;
`;

const FormPassLink = styled.a`
  font-size: 12px;
  color: var(--accent-color);
  text-decoration: underline;

  &:hover {
    color: var(--accent-color);
    text-decoration: none;
  }
`;

interface SubmitButtonProps {
  loading?: boolean;
  disabled?: boolean;
}

const FormConnection = styled.button`
  font-size: ${(props: SubmitButtonProps) => (props.loading || props.disabled ? '14px' : '14px')};
  font-size: 14px;
  text-transform: uppercase;
  line-height: 60px;
  background-color: var(--accent-color);
  color: #fff;
  text-decoration: none;
  margin: 6px 0;
  border: 2px solid var(--accent-color);
  box-sizing: border-box;
  width: 100%;
  font-weight: bold;
  cursor: pointer;
  outline: none;
  font-family: var(--font-styrene);
  transition: background 0.3s ease-in-out;

  border: none;

  &:hover {
    background-color: var(--accent-color-2);
  }

  &&& {
    &:disabled {
      background-color: lightgray;
      border: none;
      cursor: not-allowed;
    }
  }
`;
const FormRegister = styled(Register)`
  background-color: transparent;
  color: var(--accent-color);
  text-decoration: none;
  border: none;
  text-transform: none;
  font-weight: 400;
`;

const FormErrors = styled.div`
  margin-top: -0.75rem;
  margin-left: 1rem;
  display: block;
  color: rgb(255, 56, 96);
  font-weight: bold;

  ::before {
    content: '';
    background: url(${icnLoginError});
    background-size: 14px 14px;
    background-repeat: no-repeat;
    display: inline-block;
    height: 16px;
    width: 14px;
    vertical-align: sub;
    margin-right: 3px;
  }
`;

const Spinner = styled.div`
  display: block;
  margin: 20px auto;
  width: 25px;
  height: 25px;
  animation: spinAround 0.5s infinite linear;
  border: 2px solid white;
  border-radius: 15px;
  border-color: transparent transparent #fff #fff !important;
`;
